import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useMemo, useState } from "react";
import { Link } from "react-router";

import imagePlaceholder from "../../assets/images/image-placeholder.png";
import ContainerObjectList from "../ContainerObjectList/ContainerObjectList";
import DetailList from "../DetailList/DetailList";
import FileList from "../FileList/FileList";
import TabPanel from "../OrderTabs/TabPanel";

import styles from "./ArtObjectTabs.module.scss";

const ArtObjectTabs = ({
  object = {},
  documents = [],
  imageUrls = [],
  childObjects = [],
  parentObjectSerial = "",
  fromSearch = ""
}) => {
  const [value, setValue] = useState(0);
  const [imageIndex, setImageIndex] = useState(0);
  const tabletWidth = useMediaQuery("(max-width:600px)");
  const detailsTitle =
    object.type === "Container" ? "Container details" : "Object details";

  const details = useMemo(
    () => ({
      type: object.type || "-",
      ...(object.parentObjectId
        ? { inContainer: parentObjectSerial || "-" }
        : {}),
      artist: object.artist || "-",
      title: object.artworkTitle || object.containerTitle || "-",
      ed: object.ed || "-",
      inventoryNo: object.inventoryNo || "-",
      dimensions: object.dimensions || "-",
      noOfParts: object.noOfParts || "-",
      orderInDate: object.orderInDate || "-"
    }),
    [object, parentObjectSerial]
  );

  const customsDetails = useMemo(
    () => ({
      "Incoming": object.customsIncomingDocument || "-",
      "G-no.": object.customsGNumber || "-",
      "Outgoing": object.customsOutgoingDocument || "-"
    }),
    [object]
  );

  const a11yProps = (index) => {
    return {
      id: `simple-tab-${index}`,
      "aria-controls": `simple-tabpanel-${index}`
    };
  };

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const hasImages = imageUrls.length > 0;
  const activeImage = hasImages ? imageUrls[imageIndex] : "";
  const isContainer = object.type === "Container";
  const hasThumbnails = !isContainer && imageUrls.length > 1;
  const notesBlock = (
    <div
      className={`${styles.noteBlock} ${
        isContainer ? styles.noteBlockCompact : ""
      } ${!isContainer && !hasThumbnails ? styles.noteBlockCompact : ""}`}
    >
      <div
      className={`${styles.noteCard} ${
        !isContainer && !hasThumbnails ? styles.noteCardNoThumb : ""
      }`}
      >
        <div className={styles.noteSection}>
          <p className={styles.noteLabel}>Customer note</p>
          <p className={styles.noteText}>{object.customerNote || "-"}</p>
        </div>
        <div className={styles.noteSection}>
          <p className={styles.noteLabel}>Provenance</p>
          <p className={styles.noteText}>{object.provenance || "-"}</p>
        </div>
      </div>
    </div>
  );

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "tertiary.main",
          display: "flex",
          justifyContent: tabletWidth ? "flex-start" : "center"
        }}
      >
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="scrollable auto tabs"
          variant="scrollable"
          scrollButtons={false}
        >
          <Tab
            label={detailsTitle}
            {...a11yProps(0)}
            sx={{ textTransform: "unset", color: "secondary.main" }}
          />
          <Tab
            label="Documents"
            {...a11yProps(1)}
            sx={{ textTransform: "unset", color: "secondary.main" }}
          />
        </Tabs>
      </Box>

      <TabPanel value={value} index={0}>
        <div className={styles.listTabContainer}>
          <div className={styles.detailsColumn}>
            <DetailList title={detailsTitle} list={details} />
            <DetailList title="Customs" list={customsDetails} />
            {isContainer && !tabletWidth && notesBlock}
          </div>
          <div className={styles.imageContainer}>
            <div
              className={`${styles.imageBlock} ${
                isContainer ? styles.imageBlockFull : styles.imageBlockCentered
              }`}
            >
              <div className={styles.panelHeader}>
                <p className={styles.headerLine}>
                  <span>
                    {object.type === "Container" ? "Objects" : object.typeOfArt}
                  </span>
                  {object.parentObjectId && (
                    <>
                      <span className={styles.headerSeparator}> – </span>
                      <Link
                        className={styles.containerLink}
                        to={`/art-warehouse/${object.parentObjectId}`}
                        state={{ fromSearch }}
                      >
                        In container {parentObjectSerial || "-"}
                      </Link>
                    </>
                  )}
                </p>
              </div>

              <div
                className={`${styles.imagePanel} ${
                  isContainer
                    ? styles.imagePanelFull
                    : styles.imagePanelCentered
                }`}
              >
                {isContainer ? (
                  <div className={styles.containerList}>
                    <ContainerObjectList objects={childObjects} fromSearch={fromSearch} />
                  </div>
                ) : hasImages ? (
                  <>
                    <img
                      className={styles.image}
                      src={activeImage}
                      alt={`Object ${imageIndex + 1}`}
                    />
                    {imageUrls.length > 1 && (
                      <div className={styles.thumbnailRow}>
                        {imageUrls.map((url, index) => (
                          <button
                            key={`${url}-${index}`}
                            type="button"
                            className={`${styles.thumbnailButton} ${
                              index === imageIndex ? styles.thumbnailActive : ""
                            }`}
                            onClick={() => setImageIndex(index)}
                            aria-label={`View image ${index + 1}`}
                          >
                            <img
                              className={styles.thumbnail}
                              src={url}
                              alt={`Thumbnail ${index + 1}`}
                            />
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <img
                    className={styles.image}
                    src={imagePlaceholder}
                    alt="No image available"
                  />
                )}
              </div>
              {(!isContainer || tabletWidth) && notesBlock}
            </div>
          </div>
        </div>
      </TabPanel>

      <TabPanel value={value} index={1}>
        <FileList list={documents} canUpload={false} showNotes />
      </TabPanel>
    </Box>
  );
};

export default ArtObjectTabs;
